import { type ReactNode } from 'react';

import { Outlet } from 'react-router-dom';

import { cn } from '@gw/ui';

export interface CenteredContentProps {
  /**
   * Content to center. When omitted, renders React Router's `<Outlet />` so
   * `<Route element={<CenteredContent />}>` mounting works — the wrapper can be
   * a nested layout route just like AuthLayout.
   */
  children?: ReactNode;
  /**
   * Extra classes appended to the centering wrapper. Used by the AppLayout
   * `centered` variant to add the globe z-10 lift + padding; the 404 nests it
   * bare (centering only) inside the authed shell.
   */
  className?: string;
}

/**
 * CenteredContent — a composable centering wrapper that is ORTHOGONAL to chrome.
 *
 * It owns ONLY vertical+horizontal centering (`flex min-h-full items-center
 * justify-center`). It paints no background, imposes no width, and knows
 * nothing about the globe, sidebar, header, or auth state. That is the point:
 * centering composes WITH whatever shell it is nested inside rather than
 * replacing it.
 *
 * - Auth pages: the AppLayout `centered` variant renders this with the globe
 *   z-10 lift + padding appended via `className`, and AuthLayout nests beneath
 *   to supply the `max-w-md` card width — chromeless, centered over the globe.
 * - 404: nested directly inside the `default` authed shell so the page is
 *   centered regardless of authentication, with the sidebar present iff
 *   authenticated (handled entirely by the shell — no auth branching).
 */
export function CenteredContent({ children, className }: CenteredContentProps) {
  return (
    <div className={cn('flex min-h-full items-center justify-center', className)}>
      {children ?? <Outlet />}
    </div>
  );
}
